import { useEffect, useRef, useState, type RefObject } from 'react';

/** Within this many pixels of the bottom counts as at the bottom. */
const BOTTOM_SLACK_PX = 24;

const toBottom = (el: HTMLElement) => el.scrollTo({ top: el.scrollHeight });

/** SF-T-266: how far below the top a just-sent message sits. */
const ANCHOR_GAP_PX = 16;

/**
 * SF-T-222: a scrolling box that follows what's added at its bottom, as a chat does: it keeps to the bottom while
 * its content grows, stops following when the person scrolls up, and follows again once they scroll back down to
 * the bottom (or call `follow`). `content` is the element inside the box that grows. A new `resetKey` (another
 * thread) follows again from its bottom.
 */
export function useFollowScroll(box: RefObject<HTMLElement | null>, content: RefObject<HTMLElement | null>, enabled = true, resetKey?: string) {
  const [following, setFollowing] = useState(true);
  const followingRef = useRef(true);
  const [shownKey, setShownKey] = useState(resetKey);
  if (shownKey !== resetKey) {
    setShownKey(resetKey);
    setFollowing(true);
  }

  // SF-T-266: after a send, as T3 Code: once the message shows in the thread (a new [data-sent] in `content`), it sits
  // near the top and the answer fills in under it, with a spacer (`spacer`, after `content` in the box) that lets it
  // get there while the answer is short. Until it shows, the box follows the bottom. Scrolling ends it.
  const anchored = useRef<{ before: number } | null>(null);
  const spacer = useRef<HTMLDivElement | null>(null);
  const ownScroll = useRef(false);

  const follow = () => {
    anchored.current = null;
    if (spacer.current) spacer.current.style.height = '0px';
    const el = box.current;
    if (el) toBottom(el);
    followingRef.current = true;
    setFollowing(true);
  };

  /** SF-T-266: the next message to show up (after a send) goes near the top, the answer under it. */
  const anchor = () => {
    anchored.current = { before: content.current?.querySelectorAll('[data-sent]').length ?? 0 };
    followingRef.current = true;
    setFollowing(true);
    const el = box.current;
    if (el) toBottom(el);
  };

  useEffect(() => {
    const el = box.current;
    const inner = content.current;
    if (!enabled || !el || !inner) return undefined;
    followingRef.current = true;
    const place = (): boolean => {
      const a = anchored.current;
      const gap = spacer.current;
      const sent = inner.querySelectorAll('[data-sent]');
      if (!a || !gap || sent.length <= a.before) return false;
      const last = sent[sent.length - 1]!;
      const top = last.getBoundingClientRect().top - inner.getBoundingClientRect().top;
      const below = inner.getBoundingClientRect().height - top;
      gap.style.height = `${Math.max(0, el.clientHeight - ANCHOR_GAP_PX - below)}px`;
      const target = last.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - ANCHOR_GAP_PX;
      if (Math.abs(el.scrollTop - target) > 1) {
        ownScroll.current = true;
        el.scrollTop = target;
      }
      return true;
    };
    const onScroll = () => {
      if (ownScroll.current) {
        ownScroll.current = false;
        return;
      }
      if (anchored.current) {
        // The reader scrolled: the send's anchor ends.
        anchored.current = null;
        if (spacer.current) spacer.current.style.height = '0px';
      }
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_SLACK_PX;
      if (atBottom === followingRef.current) return;
      followingRef.current = atBottom;
      setFollowing(atBottom);
    };
    // Content that grows (new items, a message's full text arriving) keeps the bottom in view while following.
    const observer = new ResizeObserver(() => {
      if (place()) return;
      if (followingRef.current) toBottom(el);
    });
    observer.observe(inner);
    el.addEventListener('scroll', onScroll, { passive: true });
    if (followingRef.current) toBottom(el);
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', onScroll);
    };
  }, [box, content, enabled, resetKey]);

  return { following: enabled ? following : true, follow, anchor, spacer };
}

/** The nearest box above `el` that scrolls up and down. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
  }
  return null;
}

/**
 * SF-T-222: for a thread that scrolls with the page round it (the task pop-up on a phone, where the task, its
 * comments and its agent's thread scroll as one): `away` while some of `target` is in view but the scrolling box
 * isn't at its bottom, where the newest is; `jump` takes it there. It doesn't follow by itself: the page holds more
 * than the thread. A new `resetKey` looks again for `target` (it showed after a load).
 */
export function useScrollEnd(target: RefObject<HTMLElement | null>, enabled = true, resetKey?: string) {
  const [away, setAway] = useState(false);
  const boxRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = target.current;
    const box = el && enabled ? scrollParent(el) : null;
    boxRef.current = box;
    if (!el || !box) return undefined;
    const check = () => {
      const shown = el.getBoundingClientRect();
      const view = box.getBoundingClientRect();
      const inView = shown.top < view.bottom && shown.bottom > view.top;
      const atBottom = box.scrollHeight - box.scrollTop - box.clientHeight <= BOTTOM_SLACK_PX;
      setAway(inView && !atBottom);
    };
    const observer = new ResizeObserver(check);
    observer.observe(el);
    observer.observe(box);
    box.addEventListener('scroll', check, { passive: true });
    check();
    return () => {
      observer.disconnect();
      box.removeEventListener('scroll', check);
    };
  }, [target, enabled, resetKey]);

  const jump = () => {
    const box = boxRef.current;
    if (box) box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' });
  };
  return { away: enabled && away, jump };
}
